import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import {
  LOCAL_CURRENCY_URL_SEGMENT,
  P360_TAB_SCROLL_QUERY_PARAM,
  P360_TAB_SCROLL_QUERY_VALUE,
  PLANNING_METRIC_URL_SEGMENT,
  PLANNING_SECTION_URL_SEGMENT,
} from 'partner360/configs'

const JBP_PLAN_TAB_ID = 20
const JBP_PLAN_SCROLL_TARGET_FALLBACK = 'jbp-planning-total'
const JBP_PLAN_SCROLL_TARGET_ALIASES: Partial<Record<ProgramTypes, Record<string, string>>> = {}
const JBP_PAGE_URL = 'jbl-hpscoutso'
const JBP_PATH_PARAM_ORDER = [
  'page',
  'pid',
  'fyear',
  'ptype',
  LOCAL_CURRENCY_URL_SEGMENT,
  'jbp-tab',
  PLANNING_SECTION_URL_SEGMENT,
  PLANNING_METRIC_URL_SEGMENT,
] as const

export const JBP_PLAN_ACTION_QUERY_PARAM = 'fromJbpPlanAction'
export const JBP_PLAN_ACTION_QUERY_VALUE = '1'
export const JBP_PLAN_COMPLETED_MESSAGE_TYPE = 'JBP_PLAN_COMPLETED'

export interface IJbpPlanCompletedPayload {
  partnerId: string
  fyear: number
  offering: string
  actionParam: string
}

export interface IJbpPlanCompletedMessage {
  type: typeof JBP_PLAN_COMPLETED_MESSAGE_TYPE
  payload: IJbpPlanCompletedPayload
}

export const resolveJbpPlanActionParam = (actionParam?: string | null) =>
  actionParam?.trim() || JBP_PLAN_SCROLL_TARGET_FALLBACK

const resolveJbpPlanMetricType = (metricType?: string | null) => {
  const normalizedMetricType = metricType?.trim().toLowerCase()

  return normalizedMetricType || null
}

const normalizePathParamValue = (value?: string | number | null) => {
  if (value === undefined || value === null) {
    return null
  }

  const normalizedValue = String(value).trim()

  return normalizedValue ? normalizedValue : null
}

const splitPathSuffix = (path?: string | null) => {
  const normalizedPath = path || ''
  const queryIndex = normalizedPath.indexOf('?')
  const hashIndex = normalizedPath.indexOf('#')
  const separatorIndexes = [queryIndex, hashIndex].filter((index) => index >= 0)
  const suffixStartIndex =
    separatorIndexes.length > 0 ? Math.min(...separatorIndexes) : normalizedPath.length

  return {
    pathname: normalizedPath.slice(0, suffixStartIndex),
    suffix: normalizedPath.slice(suffixStartIndex),
  }
}

const appendQueryParam = (path: string, key: string, value: string) => {
  const { pathname, suffix } = splitPathSuffix(path)
  const hashIndex = suffix.indexOf('#')
  const search = hashIndex >= 0 ? suffix.slice(0, hashIndex) : suffix
  const hash = hashIndex >= 0 ? suffix.slice(hashIndex) : ''
  const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  searchParams.set(key, value)

  return `${pathname}?${searchParams.toString()}${hash}`
}

const parsePathParams = (path?: string | null) => {
  const { pathname } = splitPathSuffix(path)
  const pathSegments = pathname.split('/').filter(Boolean)

  const params: Record<string, string> = {}

  for (let index = 0; index < pathSegments.length - 1; index += 2) {
    params[decodeURIComponent(pathSegments[index])] = decodeURIComponent(pathSegments[index + 1])
  }

  return params
}

const buildPathFromParams = (params: Record<string, string | number | null | undefined>) => {
  const usedKeys = new Set<string>()
  const pathSegments: string[] = []

  JBP_PATH_PARAM_ORDER.forEach((key) => {
    const value = normalizePathParamValue(params[key])

    if (!value) {
      return
    }

    usedKeys.add(key)
    pathSegments.push(key, encodeURIComponent(value))
  })

  Object.entries(params).forEach(([key, value]) => {
    if (usedKeys.has(key)) {
      return
    }

    const normalizedValue = normalizePathParamValue(value)

    if (!normalizedValue) {
      return
    }

    pathSegments.push(encodeURIComponent(key), encodeURIComponent(normalizedValue))
  })

  return `/${pathSegments.join('/')}`
}

export const resolveJbpPlanScrollTarget = (
  actionParam?: string | null,
  programType?: ProgramTypes | null
) => {
  const resolvedActionParam = resolveJbpPlanActionParam(actionParam)

  if (!programType) {
    return resolvedActionParam
  }

  return JBP_PLAN_SCROLL_TARGET_ALIASES[programType]?.[resolvedActionParam] || resolvedActionParam
}

export const buildJbpPlanActionUrl = ({
  partnerId,
  fyear,
  offering,
  actionParam,
  category,
  metricType,
  includePlanningSection = true,
  isReturnFlowEnabled = false,
  shouldScrollToTab = false,
  currency,
}: {
  partnerId: string
  fyear: number
  offering: string
  actionParam?: string | null
  category?: string | null
  metricType?: string | null
  includePlanningSection?: boolean
  isReturnFlowEnabled?: boolean
  shouldScrollToTab?: boolean
  currency?: string | null
}) => {
  const resolvedMetricType = resolveJbpPlanMetricType(metricType)
  const pathname = buildPathFromParams({
    page: JBP_PAGE_URL,
    pid: partnerId,
    fyear,
    ptype: offering,
    [LOCAL_CURRENCY_URL_SEGMENT]: currency?.trim().toLowerCase() || null,
    'jbp-tab': JBP_PLAN_TAB_ID,
    [PLANNING_SECTION_URL_SEGMENT]: includePlanningSection
      ? resolveJbpPlanActionParam(category ?? actionParam)
      : null,
    [PLANNING_METRIC_URL_SEGMENT]: resolvedMetricType,
  })

  if (!isReturnFlowEnabled && !shouldScrollToTab) {
    return pathname
  }

  const searchParams = new URLSearchParams()

  if (isReturnFlowEnabled) {
    searchParams.set(JBP_PLAN_ACTION_QUERY_PARAM, JBP_PLAN_ACTION_QUERY_VALUE)
  }

  if (shouldScrollToTab) {
    searchParams.set(P360_TAB_SCROLL_QUERY_PARAM, P360_TAB_SCROLL_QUERY_VALUE)
  }

  return `${pathname}?${searchParams.toString()}`
}

const resolveJbpPartnerActionLink = (actionLink?: string | null) => {
  const normalizedActionLink = actionLink?.trim()

  if (!normalizedActionLink) {
    return null
  }

  if (normalizedActionLink.startsWith('/page/')) {
    return normalizedActionLink
  }

  return normalizedActionLink.startsWith('/') ? normalizedActionLink : `/${normalizedActionLink}`
}

export const buildJbpPartnerActionUrl = ({
  partnerId,
  fyear,
  offering,
  actionLink,
  currency,
  metricType,
  shouldScrollToTab = false,
}: {
  partnerId: string
  fyear: number
  offering: string
  actionLink?: string | null
  currency?: string | null
  metricType?: string | null
  shouldScrollToTab?: boolean
}) => {
  const resolvedActionLink = resolveJbpPartnerActionLink(actionLink)

  if (!resolvedActionLink) {
    return null
  }

  if (resolvedActionLink.startsWith('/page/')) {
    return shouldScrollToTab
      ? appendQueryParam(
          resolvedActionLink,
          P360_TAB_SCROLL_QUERY_PARAM,
          P360_TAB_SCROLL_QUERY_VALUE
        )
      : resolvedActionLink
  }

  const resolvedMetricType = resolveJbpPlanMetricType(metricType)
  const { suffix } = splitPathSuffix(resolvedActionLink)

  const actionUrl = `${buildPathFromParams({
    ...parsePathParams(resolvedActionLink),
    page: JBP_PAGE_URL,
    pid: partnerId,
    fyear,
    ptype: offering,
    [LOCAL_CURRENCY_URL_SEGMENT]: currency?.trim().toLowerCase() || null,
    [PLANNING_METRIC_URL_SEGMENT]: resolvedMetricType,
  })}${suffix}`

  return shouldScrollToTab
    ? appendQueryParam(actionUrl, P360_TAB_SCROLL_QUERY_PARAM, P360_TAB_SCROLL_QUERY_VALUE)
    : actionUrl
}

export const hasActiveOpenerWindow = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return !!window.opener && !window.opener.closed
}

export const isJbpPlanActionReturnFlow = (search: string) => {
  const searchParams = new URLSearchParams(search)

  return (
    searchParams.get(JBP_PLAN_ACTION_QUERY_PARAM) === JBP_PLAN_ACTION_QUERY_VALUE &&
    hasActiveOpenerWindow()
  )
}

export const postJbpPlanCompletedMessage = (payload: IJbpPlanCompletedPayload) => {
  if (!hasActiveOpenerWindow()) {
    return false
  }

  const message: IJbpPlanCompletedMessage = {
    type: JBP_PLAN_COMPLETED_MESSAGE_TYPE,
    payload,
  }

  try {
    window.opener.postMessage(message, window.location.origin)
  } catch {
    return false
  }

  return true
}

export const focusJbpPlanActionOpener = () => {
  if (!hasActiveOpenerWindow()) {
    return false
  }

  try {
    window.opener.focus()
  } catch {
    return false
  }

  return true
}
